/** @format */

import { useCallback, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setBos } from "redux/actions/bosAction";
import {
  deleteAlokasiSumberDanaBos,
  editAlokasiSumberDanaBos,
  getPlanningAll,
} from "services/v2/planningservice";

const CACHE_TTL = 30 * 60 * 1000;

export const useAlokasiBos = (auth: any) => {
  const dispatch = useDispatch();

  const { alokasiSumberDanaBos, cacheTime } = useSelector(
    (state: any) => ({
      alokasiSumberDanaBos: state.bos?.alokasiSumberDanaBos || [],
      cacheTime: state.bos?.cacheTime || {},
    }),
    shallowEqual,
  );

  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(
    async (force: boolean = false) => {
      if (!auth?.isTahun) return;

      const now = Date.now();
      const lastFetch = cacheTime?.alokasiBos;

      /** gunakan cache jika masih valid */
      if (
        !force &&
        alokasiSumberDanaBos.length &&
        lastFetch &&
        now - lastFetch < CACHE_TTL
      ) {
        return;
      }

      setLoading(true);

      try {
        const payload = { tahun: auth?.isTahun };

        let result =
          (await getPlanningAll("portal-bos-without-nsm", payload)) || [];

        /** filter provinsi jika bukan admin pusat */
        if (auth?.kode_role !== "admin_pusat_bos") {
          result = result.filter(
            (item: any) => item.kodeProvinsi === auth?.kode_provinsi,
          );
        }

        dispatch(
          setBos({
            alokasiSumberDanaBos: result,
            cacheTime: {
              ...cacheTime,
              alokasiBos: Date.now(),
            },
          }),
        );
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      auth?.isTahun,
      auth?.kode_role,
      auth?.kode_provinsi,
      cacheTime?.alokasiBos,
      alokasiSumberDanaBos.length,
      dispatch,
    ],
  );

  /**
   * fetch ketika tahun berubah
   */
  useEffect(() => {
    if (!auth?.isTahun) return;

    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.isTahun]);

  const editAlokasiBos = useCallback(
    async (payload: any) => {
      setLoading(true);

      try {
        const res = await editAlokasiSumberDanaBos(payload);

        if (!res) return;

        /** update redux tanpa refetch */
        const updated = [...alokasiSumberDanaBos];

        const index = updated.findIndex(
          (item: any) => item.nsm === payload.nsm,
        );

        if (index !== -1) {
          updated[index] = {
            ...updated[index],
            ...payload,
          };
        }

        dispatch(
          setBos({
            alokasiSumberDanaBos: updated,
            cacheTime: {
              ...cacheTime,
              alokasiBos: Date.now(),
            },
          }),
        );

        return res;
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [alokasiSumberDanaBos, cacheTime, dispatch],
  );

  const deleteAlokasiBos = useCallback(
    async (payload: any) => {
      setLoading(true);

      try {
        const res = await deleteAlokasiSumberDanaBos(payload);

        if (!res) return;

        const updated = alokasiSumberDanaBos.filter(
          (item: any) => item.nsm !== payload.nsm,
        );

        dispatch(
          setBos({
            alokasiSumberDanaBos: updated,
            cacheTime: {
              ...cacheTime,
              alokasiBos: Date.now(),
            },
          }),
        );

        return res;
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [alokasiSumberDanaBos, cacheTime, dispatch],
  );
  return {
    loading,
    alokasiSumberDanaBos,
    fetchData,
    editAlokasiBos,
    deleteAlokasiBos,
  };
};
